import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ProfileContext } from '../contexts/ProfileContext';
import { requestValidation, deleteUserProfile } from '../service';
import Swal from 'sweetalert2';
import { Popups } from '../components';
import { CheckIfProfileIsComplete, checkRequiredFieldsInformationForm } from '../utils/utils';
import { getTranslation } from '../utils';
import NavigationCard from '../components/ui/navigationCard';
import { VideoSection } from '../components/partials';
import { isEmpty } from 'lodash';
import { ArrowSquareOutIcon } from '@phosphor-icons/react';
import Button from '../components/ui/button';

export default function PgMyProfile() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Popup states
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showIncompletePopup, setShowIncompletePopup] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const [profile, setProfile] = useContext(ProfileContext);

  const {
    masterData: { translationPrefix, customConfigurations: { buttonRadius, colors } = {} } = {},
    profileTypeData: {
      _id: profileTypeId,
      hasVideoFunction,
      validationMethod = 'manual',
      profileUrlStructure = ''
    } = {}
  } = profile;

  // Check completion status for each section
  const isPortraitComplete = !isEmpty(profile?.image);
  const isInformationsComplete = checkRequiredFieldsInformationForm(profile)?.length === 0;
  const isStrengthsComplete = profile?.strengths?.length === 3;
  const isRolesComplete = profile?.roles?.length === 3;

  // Check if profile is fully complete
  const isProfileComplete =
    isPortraitComplete && isInformationsComplete && isStrengthsComplete && isRolesComplete;
  const showValidationMessage =
    isProfileComplete && profile?.validationType === 'unapplied' && !profile?.validationReduested;

  const showEditORPDeleteMessage =
    isProfileComplete &&
    profile?.isApproved &&
    profile?.isActive &&
    profileTypeId === '689da0000864d908a98c655c';

  const showValidateProfileButton = isProfileComplete && !profile?.validationReduested;

  const showViewProfileButton =
    profile?.validationReduested && profile?.isApproved && profile?.isActive;

  // Define navigation cards with dynamic completion status
  const navigationCards = [
    {
      number: 1,
      title: getTranslation(t, 'myProfile.portrait', translationPrefix) || 'Portrait',
      path: '/profile-image',
      isActive: !isPortraitComplete,
      isCompleted: isPortraitComplete
    },
    {
      number: 2,
      title: getTranslation(t, 'myProfile.personal', translationPrefix) || 'Informations',
      path: '/profile-data',
      isActive: isPortraitComplete && !isInformationsComplete,
      isCompleted: isInformationsComplete
    },
    {
      number: 3,
      title: getTranslation(t, 'myProfile.strengths', translationPrefix) || 'Forces préférées',
      path: '/strengths',
      isActive: isPortraitComplete && isInformationsComplete && !isStrengthsComplete,
      isCompleted: isStrengthsComplete
    },
    {
      number: 4,
      title:
        getTranslation(t, 'myProfile.roles', translationPrefix) || 'Rôles professionnels préférés',
      path: '/roles',
      isActive: isStrengthsComplete && !isRolesComplete,
      isCompleted: isRolesComplete
    }
  ];

  const handleValidation = () => {
    // Check if profile is complete
    if (!CheckIfProfileIsComplete(profile)) {
      setShowIncompletePopup(true);
      return;
    }

    // for first-time validation request
    if (profile?.validationType === 'unapplied') {
      setShowPrivacyPopup(true);
      return;
    }

    sendValidationRequest();
  };

  const handlePrivacyConfirm = async () => {
    const success = await sendValidationRequest();
    // Only close popup if request was successful
    if (success) {
      setShowPrivacyPopup(false);
    }
  };

  const sendValidationRequest = async () => {
    setIsValidating(true);
    try {
      await requestValidation();

      // Update profile context
      setProfile({
        ...profile,
        validationReduested: true,
        ...(validationMethod === 'auto' && {
          isApproved: true,
          isActive: true,
          validationType: 'first'
        })
      });

      // Show success popup - if manual validation
      if (validationMethod === 'manual') {
        setShowSuccessPopup(true);
      }

      return true;
    } catch (error) {
      console.error('Validation error:', error);
      setIsValidating(false);
      // Still show success popup even on error (409 means already validated)
      if (error?.response?.status === 409) {
        setShowSuccessPopup(true);
        return true;
      }
      return false;
    } finally {
      setIsValidating(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);

    try {
      await deleteUserProfile();

      // Clear profile data from localStorage
      try {
        localStorage.removeItem('token');
      } catch (error) {
        console.log('LS Unavailable');
      }

      // Navigate to login after successful deletion
      navigate('/');
    } catch (error) {
      console.error('Delete profile error:', error);
      setIsDeleting(false);

      // Show error message
      Swal.fire({
        title: getTranslation(t, 'popups.delete.error.title', translationPrefix),
        text: getTranslation(t, 'popups.delete.error.description', translationPrefix),
        icon: 'error',
        confirmButtonText: getTranslation(
          t,
          'popups.delete.error.confirmButton',
          translationPrefix
        ),
        iconColor: colors?.primary,
        confirmButtonColor: colors?.primary,
        didOpen: () => {
          const confirmButton = Swal.getConfirmButton();
          if (confirmButton) confirmButton.style.borderRadius = buttonRadius;
        }
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleViewProfile = () => {
    // Open profile in new tab
    const profileUrl = profileUrlStructure.replace('{{userId}}', profile._id);
    window.open(profileUrl, '_blank', 'noopener,noreferrer');
  };

  // Handle validation request from URL parameter
  useEffect(() => {
    const requestValidationParam = searchParams.get('requestValidation');
    if (requestValidationParam === 'true') {
      handleValidation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* Scrollable Body */}
      <div className="fixed top-24 bottom-20 left-0 right-0 overflow-y-auto">
        <div className="px-6 pt-8 pb-24">
          <div className="max-w-2xl mx-auto">
            <h1 style={{ color: colors?.primary || '#2B4B8C' }} className="text-3xl font-bold mb-6">
              {getTranslation(t, 'header.myProfile', translationPrefix) || 'Profil'}
            </h1>

            {/* Navigation Cards */}
            <div className="space-y-4 mb-8">
              {navigationCards.map((card, index) => (
                <NavigationCard
                  key={index}
                  number={card.number}
                  title={card.title}
                  path={card.path}
                  isActive={card.isActive}
                  isCompleted={card.isCompleted}
                  primaryColor={colors?.primary}
                  secondaryColor={colors?.secondary}
                />
              ))}
            </div>

            {/* Videos Section */}
            {hasVideoFunction && <VideoSection orgPrefix={translationPrefix} />}

            {/* View Profile Button - shown after roles section when validated */}
            {showViewProfileButton && (
              <div className="my-8">
                <button
                  onClick={handleViewProfile}
                  style={{ backgroundColor: colors?.secondary || '#D4B86A' }}
                  className="w-full text-white text-lg font-semibold px-8 py-4 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2">
                  {getTranslation(t, 'myProfile.viewProfileButton', translationPrefix) ||
                    'Voir mon profil web'}
                  <ArrowSquareOutIcon className="text-lg" />
                </button>
              </div>
            )}

            {/* Validation Message */}
            {showValidationMessage && (
              <div className="bg-white border border-gray-300 rounded-lg p-6 mt-6">
                <h2
                  className="text-xl font-bold text-gray-800 mb-4"
                  dangerouslySetInnerHTML={{
                    __html: getTranslation(t, 'validationRequestPrompt.title', translationPrefix)
                  }}
                />
                <p
                  className="text-gray-700 mb-3"
                  dangerouslySetInnerHTML={{
                    __html: getTranslation(
                      t,
                      'validationRequestPrompt.description',
                      translationPrefix
                    )
                  }}
                />
              </div>
            )}

            {/* Validation Message */}
            {showEditORPDeleteMessage && (
              <div className="bg-white my-6">
                <p
                  className="text-gray-700 mb-3 text-xl"
                  dangerouslySetInnerHTML={{
                    __html: getTranslation(
                      t,
                      'myProfile.editOrDeleteDescription',
                      translationPrefix
                    )
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-300 px-6 py-6 z-40">
        <div className="max-w-2xl mx-auto">
          {/* Show single Valider button when not yet validated - with profile view button */}

          <div className="flex flex-col items-center justify-center">
            {isProfileComplete && (
              <a
                href={profileUrlStructure.replace('{{userId}}', profile._id)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex gap-2 items-center mb-4 text-lg">
                {getTranslation(t, 'myProfile.viewProfileButton', translationPrefix)}{' '}
                <ArrowSquareOutIcon />
              </a>
            )}
            {showValidateProfileButton && (
              <div className="flex justify-center">
                <Button.ButtonRounded onClick={() => handleValidation()} loading={isValidating}>
                  {getTranslation(t, 'myProfile.validate', translationPrefix)}
                </Button.ButtonRounded>
              </div>
            )}
          </div>

          {/* Show delete button when validated and allowed */}
          {profile?.validationReduested &&
            profile?.isApproved &&
            profile?.isActive &&
            validationMethod === 'auto' && (
              <div className="flex justify-center">
                <Button.ButtonOutlined handler={() => setShowDeletePopup(true)}>
                  {getTranslation(t, 'popups.delete.triggerButton', translationPrefix) ||
                    'Supprimer'}
                </Button.ButtonOutlined>
              </div>
            )}
        </div>
      </div>

      {/* Popups */}
      {showPrivacyPopup && (
        <Popups.PrivacyConsent
          onConfirm={handlePrivacyConfirm}
          onClose={() => setShowPrivacyPopup(false)}
          orgPrefix={translationPrefix}
          isLoading={isValidating}
        />
      )}

      {showDeletePopup && (
        <Popups.DeleteConfirmation
          onConfirm={handleDeleteConfirm}
          onClose={() => setShowDeletePopup(false)}
          orgPrefix={translationPrefix}
          isLoading={isDeleting}
        />
      )}

      {showSuccessPopup && (
        <Popups.Validation
          close={() => setShowSuccessPopup(false)}
          orgPrefix={translationPrefix}
          primaryColor={colors?.primary}
        />
      )}

      {showIncompletePopup && (
        <Popups.ProfileIncomplete
          close={() => setShowIncompletePopup(false)}
          orgPrefix={translationPrefix}
        />
      )}
    </>
  );
}
